import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  Switch,
  FormControlLabel
} from '@mui/material';
import { ClientData } from '../../types/business';

interface AddClientFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (clientData: {
    clientEmail: string;
    clientName?: string;
    clientPhone?: string;
    clientDetails?: string;
    status?: 'active' | 'inactive';
  }) => Promise<{ success: boolean; error?: string }>;
  onUpdate?: (
    clientEmail: string,
    updates: {
      clientName?: string;
      clientEmail?: string;
      clientPhone?: string;
      clientDetails?: string;
      status?: 'active' | 'inactive';
    }
  ) => Promise<{ success: boolean; error?: string }>;
  isSubmitting: boolean;
  isEdit?: boolean;
  editClient?: ClientData;
}

const AddClientForm: React.FC<AddClientFormProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  isSubmitting,
  isEdit = false,
  editClient
}) => {
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientDetails, setClientDetails] = useState('');
  const [isActive, setIsActive] = useState<boolean>(true); // Using boolean for toggle
  const [error, setError] = useState<string | null>(null);
  
  const clientEmailRef = useRef<HTMLInputElement>(null);

  // Initialize form with edit data when in edit mode or reset for add mode
  useEffect(() => {
    if (open && isEdit && editClient) {
      setClientEmail(editClient.email);
      setClientName(editClient.name);
      setClientPhone(editClient.phone || '');
      setIsActive(editClient.status === 'active'); // Convert status to boolean
      setClientDetails(''); // This would need to come from API if we store client details
    } else if (open && !isEdit) {
      // Reset form for add mode
      setClientEmail('');
      setClientName('');
      setClientPhone('');
      setClientDetails('');
      setIsActive(true); // Default to active for new clients
    }
    setError(null);
  }, [open, isEdit, editClient]);
  
  const handleSubmit = async () => {
    if (!clientEmail.trim()) {
      setError('Client email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    
    setError(null);
    
    const status = isActive ? 'active' : 'inactive';
    
    let result;
    if (isEdit && onUpdate && editClient) {
      // For edit mode, only send changed fields
      const updates: any = {};
      
      if (clientName.trim() !== editClient.name) {
        updates.clientName = clientName.trim();
      }
      
      if (clientEmail.trim() !== editClient.email) {
        updates.clientEmail = clientEmail.trim();
      }
      
      if (clientPhone.trim() !== (editClient.phone || '')) {
        updates.clientPhone = clientPhone.trim() || undefined;
      }
      
      if (status !== editClient.status) {
        updates.status = status;
      }
      
      // Always include clientDetails for now since we don't have original value
      updates.clientDetails = clientDetails.trim() || undefined;
      
      result = await onUpdate(editClient.email, updates);
    } else {
      // For add mode, send all data
      result = await onSubmit({
        clientEmail: clientEmail.trim(),
        clientName: clientName.trim() || undefined,
        clientPhone: clientPhone.trim() || undefined,
        clientDetails: clientDetails.trim() || undefined,
        status: status
      });
    }

    if (result.success) {
      handleClose();
    } else {
      setError(result.error || `Failed to ${isEdit ? 'update' : 'add'} client`);
    }
  };
  
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !isSubmitting && clientEmail.trim()) {
      handleSubmit();
    }
  };
  
  const handleClose = () => {
    setClientEmail('');
    setClientName('');
    setClientPhone('');
    setClientDetails('');
    setIsActive(true);
    setError(null);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 3001 }}
      slotProps={{
        transition: {
          onEntered: () => {
            if (!isEdit) {
              clientEmailRef.current?.focus();
            }
          }
        }
      }}
    >
      <DialogTitle>{isEdit ? 'Edit Client' : 'Add New Client'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Client Information
          </Typography>
          
          <TextField
            inputRef={clientEmailRef}
            autoFocus={!isEdit}
            label="Client Email *"
            type="email"
            fullWidth
            variant="outlined"
            value={clientEmail}
            disabled={isSubmitting || isEdit} // Disable email editing in edit mode
            onChange={(e) => setClientEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            required
            helperText={isEdit ? "Email cannot be changed" : "Email address of the client you want to add"}
            error={!!error && !clientEmail.trim()}
          />
          
          <TextField
            autoFocus={isEdit}
            label="Client Name"
            type="text"
            fullWidth
            variant="outlined"
            value={clientName}
            disabled={isSubmitting}
            onChange={(e) => setClientName(e.target.value)}
            onKeyDown={handleKeyDown}
            helperText="Full name of the client (optional - will use account name if user exists)"
          />
          
          <TextField
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={clientPhone}
            disabled={isSubmitting}
            onChange={(e) => setClientPhone(e.target.value)}
            onKeyDown={handleKeyDown}
            helperText="Client's phone number (optional)"
          />
          
          <FormControl component="fieldset" disabled={isSubmitting}>
            <FormLabel component="legend" sx={{ mb: 1 }}>Client Status</FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="success"
                />
              }
              label={isActive ? "Active" : "Inactive"}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {isActive 
                ? 'Client can access your services and will receive notifications'
                : 'Client cannot access your services but relationship is maintained'
              }
            </Typography>
          </FormControl>
          
          <TextField
            label="Additional Details"
            multiline
            rows={3}
            fullWidth
            variant="standard"
            value={clientDetails}
            disabled={isSubmitting}
            onChange={(e) => setClientDetails(e.target.value)}
            helperText="Any additional notes or details about this client (optional)"
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            * Required fields
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!clientEmail.trim() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting 
            ? `${isEdit ? 'Updating' : 'Adding'} Client...` 
            : `${isEdit ? 'Update' : 'Add'} Client`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClientForm;